import type { Sides } from "@project/emerge-types";
import { VariableOptionType } from "../enum/VariableOptionType";
import type { ConnectorSetInfos } from "../interfaces/ConnectorSetInfos";
import type { VariableOptions } from "../types/VariableOptions";
import { getOpposingSide } from "../user_options/Side";

function isHingeSet(option: VariableOptions): option is ConnectorSetInfos {
  return option.variableOptionType === VariableOptionType.HINGE_SET;
}

function sidesOfHingeSet(set: ConnectorSetInfos): Sides[] {
  if (set.mirror) {
    return [set.side, getOpposingSide(set.side)];
  }
  return [set.side];
}

function sidesByGroup(
  options: readonly VariableOptions[],
): Map<string, Set<Sides>> {
  const sidesByGroup = new Map<string, Set<Sides>>();
  for (const option of options) {
    if (!isHingeSet(option) || !option.group) {
      continue;
    }
    const sides = sidesByGroup.get(option.group) ?? new Set<Sides>();
    for (const side of sidesOfHingeSet(option)) {
      sides.add(side);
    }
    sidesByGroup.set(option.group, sides);
  }
  return sidesByGroup;
}

function indexHingeSets(
  options: readonly VariableOptions[],
): Map<string, ConnectorSetInfos> {
  const byUuid = new Map<string, ConnectorSetInfos>();
  for (const option of options) {
    if (isHingeSet(option)) {
      byUuid.set(option.uuid, option);
    }
  }
  return byUuid;
}

/**
 * Whether both lists hold at least one hinge row with the same uuid, i.e. they
 * describe the same position and one can serve as the other's baseline.
 */
export function sharesHingeRow(
  baseline: readonly VariableOptions[],
  next: readonly VariableOptions[],
): boolean {
  const nextRows = indexHingeSets(next);
  if (nextRows.size === 0) {
    return false;
  }
  return baseline.some(
    (option) => isHingeSet(option) && nextRows.has(option.uuid),
  );
}

function onlySide(sides: Set<Sides> | undefined): Sides | undefined {
  if (sides?.size !== 1) {
    return undefined;
  }
  return sides.values().next().value;
}

/** Cover groups whose hinges do not share one edge. */
export function groupsWithSplitHingeAxis(
  options: readonly VariableOptions[],
): string[] {
  const split: string[] = [];
  for (const [group, sides] of sidesByGroup(options)) {
    if (sides.size > 1) {
      split.push(group);
    }
  }
  return split;
}

/** Side picked by a row that stayed on its cover and changed edge. */
function sideChosenForGroup(
  next: readonly VariableOptions[],
  previousByUuid: ReadonlyMap<string, ConnectorSetInfos>,
): Map<string, Sides> {
  const targetByGroup = new Map<string, Sides>();
  for (const option of next) {
    if (!isHingeSet(option) || !option.group) {
      continue;
    }
    const prior = previousByUuid.get(option.uuid);
    if (!prior || prior.group !== option.group || prior.side === option.side) {
      continue;
    }
    targetByGroup.set(option.group, option.side);
  }
  return targetByGroup;
}

/**
 * Edge a row should use: the side just chosen on its cover, or the single
 * edge of a cover it is joining. Undefined when the row stays put.
 */
function edgeForHinge(
  option: ConnectorSetInfos,
  previousByUuid: ReadonlyMap<string, ConnectorSetInfos>,
  previousSides: ReadonlyMap<string, Set<Sides>>,
  targetByGroup: ReadonlyMap<string, Sides>,
): Sides | undefined {
  const chosen = targetByGroup.get(option.group);
  if (chosen !== undefined) {
    return chosen;
  }
  const prior = previousByUuid.get(option.uuid);
  if (!prior || prior.group !== option.group) {
    return onlySide(previousSides.get(option.group));
  }
  return undefined;
}

/**
 * Mirror that the row did not already have on this cover. A loaded mirrored
 * row stays as stored while it stays on its cover; on another cover it is new.
 */
function mirrorWasAdded(
  option: ConnectorSetInfos,
  prior: ConnectorSetInfos | undefined,
): boolean {
  return !!option.mirror && (!prior?.mirror || prior.group !== option.group);
}

/**
 * When one hinge row of a cover changes edge, every hinge row of that cover
 * follows. A new row, or a row moved onto another cover, joins the edge that
 * cover already uses; a row moved onto a cover without hinges keeps its edge.
 * A mirror that was just turned on, or that a row brings onto another cover,
 * is cleared, so it cannot open a second edge. Returns null when nothing had
 * to move.
 */
export function alignHingeGroupSides(
  previous: readonly VariableOptions[] | undefined,
  next: readonly VariableOptions[],
): VariableOptions[] | null {
  if (!previous) {
    return null;
  }

  const previousByUuid = indexHingeSets(previous);
  const targetByGroup = sideChosenForGroup(next, previousByUuid);
  const previousSides = sidesByGroup(previous);
  let changed = false;
  const aligned = next.map((option) => {
    if (!isHingeSet(option) || !option.group) {
      return option;
    }
    const target = edgeForHinge(
      option,
      previousByUuid,
      previousSides,
      targetByGroup,
    );
    const prior = previousByUuid.get(option.uuid);
    if (target === undefined) {
      if (!mirrorWasAdded(option, prior)) {
        return option;
      }
      changed = true;
      return { ...option, mirror: false };
    }
    if (option.side === target && !option.mirror) {
      return option;
    }
    changed = true;
    return { ...option, side: target, mirror: false };
  });
  return changed ? aligned : null;
}
